import * as pdfjsLib from 'pdfjs-dist'
import Tooltip from 'bootstrap/js/dist/tooltip'
import workerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url'
import { trackToolUsage } from './analytics.js'
import { annotationMethods } from './birme/annotations.js'
import { historyMethods } from './birme/history.js'
import { interactionMethods } from './birme/interactions.js'
import { pageMethods } from './birme/pages.js'
import { saveMethods } from './birme/save.js'
import { searchMethods } from './birme/search.js'
import { textEditingMethods } from './birme/text-editing.js'
import { visualAnnotationMethods } from './birme/visual-annotations.js'
import { clamp, maxAutoZoom, wrapText } from './birme/utils.js'

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl

export class BirmePDF {
  // Feature modules are composed onto the prototype below.
  [member: string]: any

  constructor() {
    this.fileName = 'document.pdf'
    this.sourceBytes = null
    this.pdf = null
    this.documents = new Map()
    this.nextDocumentId = 1
    this.nextPageId = 1
    this.pages = []
    this.currentIndex = 0
    this.zoom = 1
    this.autoZoom = true
    this.resizeTimer = null
    this.renderTask = null
    this.textMode = false
    this.markMode = false
    this.selectMode = false
    this.editTextMode = false
    this.selectedTextItem = null
    this.selectedAnnotationId = null
    this.selectedMarkupId = null
    this.markupMode = null
    this.markupInteraction = null
    this.markupDefaults = { color: '#f4c430', opacity: 0.45, strokeWidth: 3 }
    this.editingTextItemId = null
    this.editingAnnotationId = null
    this.focusInPlaceEditor = false
    this.focusAnnotationEditor = false
    this.textPresetEditable = true
    this.textInsertValue = ''
    this.panelEditRenderTimer = null
    this.textStyleRenderTimer = null
    this.savedState = ''
    this.undoStack = []
    this.redoStack = []
    this.historyLimit = 100
    this.restoringHistory = false
    this.sourceFontFaces = new Map()
    this.searchTextCache = new Map()
    this.searchResults = []
    this.searchResultIndex = -1
    this.searchRequestId = 0
    this.sourcePasswordProtected = false
    this.passwordLoadingTask = null
    this.passwordUpdate = null
    this.passwordCancelled = false

    this.canvas = document.querySelector('.pdf-canvas')
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })
    this.stage = document.querySelector('.page-stage')
    this.textEditLayer = document.querySelector('.text-edit-layer')
    this.annotationLayer = document.querySelector('.annotation-layer')
    this.markupLayer = document.querySelector('.markup-layer')
    this.textInsertPreview = null
    this.textInsertPreviewPosition = null
    this.annotationDrag = null
    this.sheet = this.ensurePageSheet()
    this.pageList = document.querySelector('.page-list')
    this.status = document.querySelector('.status')
    this.tooltips = []
    this.passwordDialog = document.querySelector('.password-dialog')
    this.passwordForm = document.querySelector('.password-form')
    this.passwordInput = document.querySelector('#pdf-password')
    this.passwordError = document.querySelector('.password-error')
    this.pdfChoiceDialog = document.querySelector('.pdf-choice-dialog')
    this.pdfChoiceInput = document.querySelector('#pdf-upload-toolbar')
    this.searchBar = document.querySelector('.search-bar')
    this.searchInput = document.querySelector('.search-input')
    this.searchCount = document.querySelector('.search-count')
    this.markupOptions = document.querySelector('.markup-options')
    this.imageInput = document.querySelector('.image-input')

    document.querySelectorAll<HTMLInputElement>('.inputfile').forEach(input => {
      input.addEventListener('click', () => { input.value = '' })
      input.addEventListener('change', event => {
        const target = event.currentTarget as HTMLInputElement
        const files = Array.from(target.files || [])
        if (files.length) trackToolUsage(target.dataset.openMode === 'append' && this.pdf ? 'add-pdf' : 'open-pdf', 'file-picker')
        this.openFiles(files, target.dataset.openMode !== 'append' || !this.pdf)
      })
    })

    document.addEventListener('click', event => {
      const control = (event.target as Element | null)?.closest<HTMLElement>('[data-action]')
      if (control?.getAttribute('aria-disabled') === 'true') {
        event.preventDefault()
        return
      }
      if (control?.dataset.action) this.handleAction(control.dataset.action)
    })
    document.addEventListener('keydown', event => {
      const modifier = event.ctrlKey || event.metaKey
      const key = event.key.toLowerCase()
      if (modifier && key === 's') {
        event.preventDefault()
        if (this.pdf) {
          trackToolUsage('save', 'keyboard')
          this.save()
        }
        return
      }
      if (modifier && key === 'f' && this.pdf) {
        event.preventDefault()
        trackToolUsage('search', 'keyboard')
        this.openSearch()
        return
      }
      if (modifier && key === 'z') {
        event.preventDefault()
        if (event.shiftKey) {
          trackToolUsage('redo', 'keyboard')
          this.redo()
        } else {
          trackToolUsage('undo', 'keyboard')
          this.undo()
        }
        return
      }
      if (modifier && key === 'y') {
        event.preventDefault()
        trackToolUsage('redo', 'keyboard')
        this.redo()
        return
      }
      if (this.shouldHandleAnnotationNudge(event)) {
        event.preventDefault()
        this.nudgeSelectedAnnotation(event.key, event.shiftKey)
        return
      }
      if (event.key === 'Escape') this.exitCurrentEdit()
      if ((event.key === 'Delete' || event.key === 'Backspace') && this.shouldHandleDeleteKey(event)) {
        event.preventDefault()
        this.deleteSelectedText()
      }
    })
    window.addEventListener('resize', () => this.handleWindowResize())

    this.passwordForm?.addEventListener('submit', event => this.submitPdfPassword(event))
    this.passwordDialog?.querySelector('.password-cancel')?.addEventListener('click', () => this.cancelPdfPassword())
    this.passwordDialog?.addEventListener('cancel', event => {
      event.preventDefault()
      this.cancelPdfPassword()
    })
    document.querySelector('.pdf-choice-open')?.addEventListener('click', () => this.pdfChoiceDialog?.showModal())
    this.pdfChoiceDialog?.querySelector('.pdf-choice-cancel')?.addEventListener('click', () => this.pdfChoiceDialog.close())
    this.pdfChoiceDialog?.querySelector('.pdf-choice-replace')?.addEventListener('click', () => this.chooseToolbarPdfs('replace'))
    this.pdfChoiceDialog?.querySelector('.pdf-choice-append')?.addEventListener('click', () => this.chooseToolbarPdfs('append'))
    this.searchInput?.addEventListener('input', () => this.updateSearch())
    this.searchBar?.addEventListener('submit', event => {
      event.preventDefault()
      this.stepSearch((event as SubmitEvent).submitter?.dataset.action === 'search-prev' ? -1 : 1)
    })
    this.searchInput?.addEventListener('keydown', event => {
      if (event.key === 'Enter') {
        event.preventDefault()
        this.stepSearch(event.shiftKey ? -1 : 1)
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        this.closeSearch()
      }
    })
    this.initVisualAnnotations()

    this.stage.addEventListener('click', event => this.handleStageClick(event))
    this.stage.addEventListener('pointermove', event => this.updateTextInsertPreview(event))
    this.stage.addEventListener('pointerleave', () => this.hideTextInsertPreview())
    document.addEventListener('pointermove', event => this.moveAnnotation(event))
    document.addEventListener('pointermove', event => this.moveMarkupInteraction(event))
    document.addEventListener('pointerup', event => {
      this.endAnnotationMove()
      this.endMarkupInteraction(event)
    })
    this.stage.addEventListener('pointerdown', event => this.startMarkupInteraction(event))

    this.initTooltips()
    this.openTestPdfInDev()
  }

  ensurePageSheet() {
    let sheet = document.querySelector('.page-sheet')
    if (sheet) return sheet

    sheet = document.createElement('div')
    sheet.className = 'page-sheet'
    this.stage.append(sheet)
    sheet.append(this.canvas, this.markupLayer, this.textEditLayer, this.annotationLayer)
    return sheet
  }

  async openTestPdfInDev() {
    if (!import.meta.env.DEV) return

    try {
      const response = await fetch('/static/test.pdf')
      const contentType = response.headers.get('content-type') || ''
      if (!response.ok || !contentType.includes('application/pdf')) return
      const blob = await response.blob()
      await this.openFile(new File([blob], 'test.pdf', { type: 'application/pdf' }))
    } catch (error) {
      console.warn('Test PDF could not be loaded.', error)
    }
  }

  initTooltips() {
    this.tooltips = Array.from(document.querySelectorAll('.viewer-toolbar [data-bs-title]'), element => new Tooltip(element, {
      boundary: document.body,
      container: 'body',
      delay: { show: 80, hide: 0 },
      placement: 'top',
      trigger: 'hover',
    }))
  }

  setToolbarDisabled(element, disabled) {
    if (!element) return
    element.setAttribute('aria-disabled', String(disabled))
  }

  setToolbarTooltip(element, label) {
    element.setAttribute('data-bs-title', label)
    const tooltip = Tooltip.getInstance(element)
    if (tooltip) tooltip.setContent({ '.tooltip-inner': label })
  }


  async loadPdfFile(file) {
    let loadingTask = null
    try {
      const sourceBytes = await file.arrayBuffer()
      loadingTask = pdfjsLib.getDocument({ data: sourceBytes.slice(0), fontExtraProperties: true })
      this.passwordLoadingTask = loadingTask
      this.passwordCancelled = false
      let passwordProtected = false
      loadingTask.onPassword = (updatePassword, reason) => {
        passwordProtected = true
        this.requestPdfPassword(updatePassword, reason)
      }
      const pdf = await loadingTask.promise
      if (this.passwordCancelled) return null
      this.closePdfPasswordDialog()
      return { file, sourceBytes, pdf, passwordProtected }
    } catch (error) {
      if (this.passwordCancelled) {
        this.setStatus('Opening the protected PDF was cancelled.')
        return
      }
      console.error(error)
      this.setStatus(`Could not open PDF: ${error?.message || 'unknown error'}`)
      return null
    } finally {
      if (this.passwordLoadingTask === loadingTask) this.passwordLoadingTask = null
      this.passwordUpdate = null
    }
  }

  async openFile(file) {
    return this.openFiles(file ? [file] : [], true)
  }

  chooseToolbarPdfs(mode) {
    if (!this.pdfChoiceInput) return
    this.pdfChoiceInput.dataset.openMode = mode
    this.pdfChoiceDialog?.close()
    this.pdfChoiceInput.click()
  }

  async openFiles(files, replaceWorkspace = false) {
    const pdfFiles = files.filter(file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))
    if (!pdfFiles.length) {
      if (files.length) this.setStatus('Please choose PDF files.')
      return
    }

    let workspaceReplaced = false
    let importedPages = 0
    let skippedFiles = files.length - pdfFiles.length
    const historySnapshot = this.pdf && !replaceWorkspace ? this.captureHistoryState() : null

    for (const file of pdfFiles) {
      this.setStatus(`Opening ${file.name}…`)
      const loaded = await this.loadPdfFile(file)
      if (!loaded) {
        skippedFiles += 1
        continue
      }
      const indexes = Array.from({ length: loaded.pdf.numPages }, (_, index) => index)

      if (replaceWorkspace && !workspaceReplaced) {
        this.clearSourceFontFaces()
        this.clearSearch()
        for (const document of this.documents.values()) document.pdf?.destroy?.().catch?.(() => {})
        this.documents.clear()
        this.pages = []
        this.fileName = file.name
        workspaceReplaced = true
      }

      const documentId = `document-${this.nextDocumentId++}`
      this.documents.set(documentId, { id: documentId, ...loaded })
      for (const sourceIndex of indexes) {
        this.pages.push({
          id: `page-${this.nextPageId++}`,
          documentId,
          sourceIndex,
          rotation: 0,
          deleted: false,
          annotations: [],
          markups: [],
          textItems: [],
          textEdits: {},
        })
      }
      importedPages += indexes.length
    }

    if (!importedPages) {
      this.setStatus(skippedFiles ? 'No PDF pages were added.' : 'No files selected.')
      return
    }
    if (historySnapshot) this.recordHistorySnapshot(historySnapshot)
    this.pdf = this.documents.values().next().value?.pdf || null
    this.sourceBytes = this.documents.values().next().value?.sourceBytes || null
    this.sourcePasswordProtected = Array.from(this.documents.values()).some((document: any) => document.passwordProtected)
    if (this.documents.size > 1 && !/-merged\.pdf$/i.test(this.fileName)) {
      this.fileName = this.fileName.replace(/\.pdf$/i, '') + '-merged.pdf'
    }
    this.currentIndex = replaceWorkspace ? 0 : Math.max(0, this.visiblePages.length - importedPages)
    this.autoZoom = true
    this.selectedTextItem = null
    this.selectedAnnotationId = null
    this.focusAnnotationEditor = false
    if (replaceWorkspace) {
      this.savedState = this.serializeState()
      this.clearHistory()
    }
    document.body.classList.add('not-empty')
    document.body.classList.remove('show-about')
    this.enableControls(true)
    this.setSelectMode(true)
    this.setEditTextMode(true)
    this.setStatus(`${importedPages} page${importedPages === 1 ? '' : 's'} added${skippedFiles ? `; ${skippedFiles} file${skippedFiles === 1 ? '' : 's'} skipped` : ''}.`)
    await this.renderThumbnails()
    await this.renderCurrentPage()
  }

  documentForPage(pageState) {
    return pageState ? this.documents.get(pageState.documentId) : null
  }

  pdfForPage(pageState) {
    return this.documentForPage(pageState)?.pdf || null
  }

  requestPdfPassword(updatePassword, reason) {
    this.passwordUpdate = updatePassword
    this.passwordInput.value = ''
    this.passwordError.hidden = reason !== pdfjsLib.PasswordResponses.INCORRECT_PASSWORD
    if (!this.passwordDialog.open) this.passwordDialog.showModal()
    requestAnimationFrame(() => this.passwordInput.focus())
  }

  submitPdfPassword(event) {
    event.preventDefault()
    if (!this.passwordUpdate) return
    const password = this.passwordInput.value
    this.passwordInput.value = ''
    if (!password) {
      this.passwordInput.focus()
      return
    }
    const updatePassword = this.passwordUpdate
    this.passwordUpdate = null
    this.passwordError.hidden = true
    updatePassword(password)
  }

  async cancelPdfPassword() {
    this.passwordCancelled = true
    this.passwordUpdate = null
    this.closePdfPasswordDialog()
    const loadingTask = this.passwordLoadingTask
    this.passwordLoadingTask = null
    if (loadingTask) await loadingTask.destroy().catch(() => {})
  }

  closePdfPasswordDialog() {
    this.passwordInput.value = ''
    this.passwordError.hidden = true
    if (this.passwordDialog.open) this.passwordDialog.close()
  }

  async renderCurrentPage() {
    const pageState = this.visiblePages[this.currentIndex]
    if (!this.pdf || !pageState) return
    if (this.selectedTextItem?.pageId !== pageState.id) {
      this.selectedTextItem = null
      this.editingTextItemId = null
      this.resetTextEditor()
    }
    if (this.selectedAnnotationId && !pageState.annotations.some(annotation => annotation.id === this.selectedAnnotationId)) {
      this.selectedAnnotationId = null
      this.editingAnnotationId = null
      this.focusAnnotationEditor = false
    }
    if (this.selectedMarkupId && !(pageState.markups || []).some(markup => markup.id === this.selectedMarkupId)) {
      this.selectedMarkupId = null
      this.markupInteraction = null
    }

    if (this.renderTask) {
      this.renderTask.cancel()
      this.renderTask = null
    }

    const page = await this.pdfForPage(pageState).getPage(pageState.sourceIndex + 1)
    if (this.autoZoom) this.zoom = this.fitZoomForPage(page, pageState)
    const viewport = page.getViewport({ scale: this.zoom, rotation: page.rotate + pageState.rotation })
    this.canvas.width = Math.floor(viewport.width)
    this.canvas.height = Math.floor(viewport.height)
    this.stage.style.setProperty('--page-width', `${viewport.width}px`)
    this.stage.style.setProperty('--page-height', `${viewport.height}px`)
    this.sheet.style.width = `${viewport.width}px`
    this.sheet.style.height = `${viewport.height}px`
    this.annotationLayer.style.width = `${viewport.width}px`
    this.annotationLayer.style.height = `${viewport.height}px`
    this.markupLayer.setAttribute('width', String(viewport.width))
    this.markupLayer.setAttribute('height', String(viewport.height))
    this.markupLayer.setAttribute('viewBox', `0 0 ${viewport.width} ${viewport.height}`)
    this.textEditLayer.style.width = `${viewport.width}px`
    this.textEditLayer.style.height = `${viewport.height}px`
    this.textEditLayer.innerHTML = ''
    this.annotationLayer.innerHTML = ''
    this.markupLayer.innerHTML = ''

    this.renderTask = page.render({ canvasContext: this.ctx, viewport })
    try {
      await this.renderTask.promise
    } catch (error) {
      if (error?.name !== 'RenderingCancelledException') throw error
    }
    this.renderTask = null
    this.renderTextEditPreview(pageState, viewport)
    await this.renderTextEditLayer(page, viewport, pageState)
    this.renderAnnotations(pageState)
    this.renderVisualAnnotations(pageState)
    this.syncAnnotationTools()
    this.syncMarkupOptions()
    this.updateTextInsertPreview()
    this.updateCounters()
    this.updateDirtyState()
    this.highlightThumb()
  }

  fitZoomForPage(page, pageState) {
    const viewport = page.getViewport({ scale: 1, rotation: page.rotate + pageState.rotation })
    const style = getComputedStyle(this.stage)
    const horizontalPadding = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight)
    const availableWidth = Math.max(1, this.stage.clientWidth - horizontalPadding)
    const fit = availableWidth / viewport.width

    return clamp(Number(fit.toFixed(2)), 0.5, maxAutoZoom)
  }

  handleWindowResize() {
    if (!this.pdf || !this.autoZoom) return
    clearTimeout(this.resizeTimer)
    this.resizeTimer = setTimeout(() => this.renderCurrentPage(), 120)
  }







  get visiblePages() {
    return this.pages.filter(page => !page.deleted)
  }









}

Object.assign(
  BirmePDF.prototype,
  { wrapText },
  textEditingMethods,
  annotationMethods,
  pageMethods,
  interactionMethods,
  historyMethods,
  saveMethods,
  searchMethods,
  visualAnnotationMethods,
)
